import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import Content from 'assets/js/components/Content';
import * as GivebutterPlusEndTrialDialog from 'assets/js/pages/account-settings/account-about/givebutter-plus/GivebutterPlusEndTrialDialog';
import * as GivebutterPlusYourTrialHasEndedDialog from 'assets/js/pages/account-settings/account-about/givebutter-plus/GivebutterPlusYourTrialHasEndedDialog';
import { AccountOverviewChart } from 'components/account-overview-chart';
import { PermissionErrorBoundary } from 'components/error-boundaries/PermissionErrorBoundary';
import { FirstTimeSupporters } from 'components/first-time-supporters';
import { OnboardingChecklist } from 'components/onboarding-checklist';
import { useOnboardingTasks } from 'components/onboarding-checklist/useOnboardingTasks';
import { ProfilePanel } from 'components/profile-panel/ProfilePanel';
import { RecommendedActions } from 'components/recommended-actions/RecommendedActions';
import { RequiredHomepageChecklist } from 'components/required-checklist';
import { ChecklistHomepageConfetti } from 'components/required-checklist/ChecklistHomepageConfetti';
import { useRequiredChecklistVisibility } from 'components/required-checklist/useRequiredChecklistVisibility';
import { ShareCard } from 'components/share-card';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { usePersonalizedDashboardPosthog } from 'customHooks/usePersonalizedDashboardPosthog';
import { AnimatePresence, motion } from 'framer-motion';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Callouts } from 'pages/home/Callouts';
import { twJoin } from 'tailwind-merge';

const Home = () => {
    const {
        mobileAppRelease = false,
        shareCardHomePage = false,
        showRecommendedActions = false,
        showRequiredChecklist = false,
        personalizedDashboard = false,
    } = useFlags();
    const {
        accountId,
        type,
        isEinVerified,
        isCurrentlyOnFreeTrial,
        isTrialEndedWithin24Hours,
        isFreeTrialExpired,
        validGivebutterPlusPlan,
    } = useCurrentAccount();
    const { shouldShowChecklists } = useRequiredChecklistVisibility();

    const {
        hasDismissedNonProfitEinCallout,
        setHasDismissedNonprofitEinCallout,
        hasDismissedGivebutterPlusFreeTrialDialog,
        hasDismissedTrialEndedDialog,
    } = useLocalStorage(state => state);

    const { hasSeenChecklistCompleteDialog } = useAccountLocalStorage(
        ({ hasSeenChecklistCompleteDialog }) => ({ hasSeenChecklistCompleteDialog }),
    );

    const [showFreeTrialDialog, setShowFreeTrialDialog] = useState(
        isCurrentlyOnFreeTrial && !hasDismissedGivebutterPlusFreeTrialDialog,
    );
    const [showFreeTrialDialogEnded, setShowFreeTrialDialogEnded] = useState(
        isFreeTrialExpired &&
            isTrialEndedWithin24Hours &&
            !hasDismissedTrialEndedDialog &&
            !validGivebutterPlusPlan,
    );

    const showNonProfitEinCallout =
        !hasDismissedNonProfitEinCallout &&
        !isEinVerified &&
        (type === 'individual' || type === 'company');

    return (
        <div className={'flex flex-col gap-8'}>
            <PermissionErrorBoundary FallbackComponent={null}>
                {showRequiredChecklist && personalizedDashboard ? (
                    <AnimatePresence initial={false} mode="popLayout">
                        {
                            <motion.div
                                key={
                                    hasSeenChecklistCompleteDialog
                                        ? 'full-homepage'
                                        : 'onboarding-checklist-homepage'
                                }
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                {shouldShowChecklists ? (
                                    <RequiredHomepageChecklist />
                                ) : (
                                    <Content className="flex flex-col gap-8">
                                        <OnboardingTasksSection />
                                    </Content>
                                )}
                            </motion.div>
                        }
                    </AnimatePresence>
                ) : (
                    <OnboardingTasksSection />
                )}
            </PermissionErrorBoundary>
            {!(showRecommendedActions && personalizedDashboard) && mobileAppRelease && <Callouts />}
            {showNonProfitEinCallout && (
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <p className="text-lg font-medium">Are you a nonprofit?</p>
                    <p className="mb-4 text-gray-500">
                        Enter your EIN to have access to exclusive benefits, like faster settlement
                        times, ACH payments, and more!
                    </p>

                    <Button
                        variant="outline"
                        color="neutral"
                        className="mr-4 !inline"
                        onClick={() => {
                            setHasDismissedNonprofitEinCallout(true);
                        }}
                    >
                        Dismiss
                    </Button>

                    <Link
                        to={`/accounts/${accountId}/settings/about/verify#verifyNonprofit`}
                        className="!inline-block align-top"
                    >
                        <Button variant="solid">Enter EIN</Button>
                    </Link>
                </div>
            )}
            {shareCardHomePage && <ShareCard />}
            {showRecommendedActions && personalizedDashboard && <RecommendedActions />}
            <GivebutterPlusEndTrialDialog.Root
                open={showFreeTrialDialog}
                onOpenChange={open => setShowFreeTrialDialog(open)}
            />
            <GivebutterPlusYourTrialHasEndedDialog.Root
                open={showFreeTrialDialogEnded}
                onOpenChange={open => setShowFreeTrialDialogEnded(open)}
            />
        </div>
    );
};

const OnboardingTasksSection = () => {
    const { firstName } = useCurrentUser();
    const { tasks } = useOnboardingTasks();
    const {
        firstTimeDonors = false,
        personalizedDashboard = false,
        profilePanel = false,
    } = useFlags();
    return (
        <>
            {(!!tasks?.didAckCta || !!tasks?.hasRaisedOverMax) && (
                <h1 className="text-2xl font-medium">Welcome, {firstName}!</h1>
            )}
            {!personalizedDashboard && (!tasks?.didAckCta || !tasks?.hasRaisedOverMax) && (
                <Content className="max-w-3xl">
                    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-0 shadow-sm">
                        <OnboardingChecklist />
                    </div>
                </Content>
            )}
            {!!tasks?.hasRaisedFirstDollar && (
                <>
                    {personalizedDashboard && profilePanel ? (
                        <div className="flex flex-col gap-8">
                            <div className="flex flex-col gap-8 sm:flex-row sm:gap-6">
                                <ProfilePanel />
                                <AccountOverviewChart />
                            </div>
                            {firstTimeDonors && <FirstTimeSupporters />}
                        </div>
                    ) : (
                        <>
                            <AccountOverviewChart />
                            {firstTimeDonors && <FirstTimeSupporters />}
                        </>
                    )}
                </>
            )}
        </>
    );
};

const WrappedHome = () => {
    const { personalizedDashboard = false } = useFlags();
    const { shouldShowHomepageConfetti } = useRequiredChecklistVisibility();
    usePersonalizedDashboardPosthog();

    return (
        <>
            <Content className={twJoin(personalizedDashboard ? 'max-w-5xl' : 'max-w-3xl')}>
                <Home />
            </Content>
            {shouldShowHomepageConfetti && <ChecklistHomepageConfetti />}
        </>
    );
};

export default WrappedHome;
